import * as React from "react";

import dayjs from "dayjs";
import _ from "lodash";

import Sentry from "@similarweb/sentry-logger";
import { LegendWithOneLineCheckboxFlex, SegmentedControl } from "@similarweb/ui-components";
import { LineGraph } from "@similarweb/pro-logic-components";

import { swSettings } from "app/common/services/swSettings";
import { InfoIcon } from "pro-features/components/BoxTitle/src/BoxTitle";
import combineConfigs from "pro-features/components/Chart/src/combineConfigs";
import { PeriodOverPeriodChart } from "pro-features/components/Chart/src/components/PeriodOverPeriodChart/PeriodOverPeriodChart";
import yAxisLabelsConfig from "pro-features/components/Chart/src/configs/yAxis/yAxisLabelsConfig";
import { MMXAlertWithPlainTooltip } from "app/components/MMXAlertWithPlainTooltip";
import { NoDataLandscape } from "pro-features/components/NoData/src/NoData";
import { Legends } from "app/components/React/Legends/Legends";
import { PlainTooltip } from "app/components/React/Tooltip/PlainTooltip/PlainTooltip";
import { CHART_COLORS } from "app/constants/ChartColors";
import { i18nFilter, translate } from "app/filters/i18nFilter";
import { minVisitsAbbrFilter, numberFilter, percentageSignFilter, timeFilter } from "app/filters/ngFilters";
import {
    filtersList,
    getChartConfig,
} from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelAnalysisChart/ChannelAnalysisChartConfig";
import {
    getYaxisFormat,
    shouldShowGoogleAiModeAnnotations,
} from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelAnalysisChart/ChannelAnalysisChartConfigHelpers";
import {
    ChannelsBar,
    ChartContainer,
    ChartHeaderContainer,
    ChartLegendsContainer,
    ChartWrapper,
    CheckBoxContainer,
    ContentWrapper,
    LegendsTitle,
    NoDataWrapper,
    StyledLegendWrapper,
    StyledTab,
    StyledTabIcon,
    StyledTabList,
    Wrapper,
} from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelAnalysisChart/StyledComponents";
import { adaptCompareSingleData } from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelAnalysisPoPSingle";
import {
    isDurationAfterSplit,
    removeSearchChannelTotal,
    removeSplitSearchChannelsTotal,
    removeDataFromGraphData,
} from "app/pages/website-analysis/traffic-sources/mmx/components/common/MobileSplit-utils";
import type { IInsight } from "app/pages/website-analysis/traffic-sources/mmx/components/InsightsAssistant/InsightsContainer";
import InsightsContainer from "app/pages/website-analysis/traffic-sources/mmx/components/InsightsAssistant/InsightsContainer";
import { PdfExportService } from "app/services/PdfExportService";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

import { adaptCompareData, ChannelAnalysisPoPCompare } from "../ChannelAnalysisPoPCompare/ChannelAnalysisPoPCompare";
import { allTrackers } from "app/services/track/track";

const gran = {
    Monthly: "MonthlyData",
    Weekly: "WeeklyData",
    Daily: "DailyData",
};

const MAX_DAILY_DATA_POINTS_WITH_MARKERS = 2;

const CHANNEL_SHORT_LABEL_KEY: Record<string, string> = {
    "Search - Organic": "channels.label.short.org.search",
    "Organic Search": "channels.label.short.org.search",
    "Search - Paid": "channels.label.short.paid.search",
    "Paid Search": "channels.label.short.paid.search",
    "Social - Organic": "channels.label.short.org.social",
    "Social - Paid": "channels.label.short.paid.social",
};

export class ChannelAnalysisChart extends React.Component<any, any> {
    private chart;

    private pdfExportService;

    private legendItems;

    private newMMXAlgoStartData: string;

    private filterChannels = this.props?.filterChannels ?? filtersList[this.props.webSource];

    constructor(props) {
        super(props);
        this.newMMXAlgoStartData = swSettings.current.resources.NewAlgoMMX;
        this.state = {
            isCompare: this.props.keys.length > 1,
            checkboxFilters: this.getCheckboxFilters(),
            selectedVisualizationIndex: 1,
            insights: this.getEnrichedInsights(),
            insightCardClicked: false,
            isAllLegendsChecked: true,
            isError: false,

            selectedChannel: this.getSelectedChannelIndex(),
        };
        this.pdfExportService = PdfExportService;
    }

    private isMobileSource = this.props.webSource === devicesTypes.MOBILE;

    private isTotalSource = this.props.webSource === devicesTypes.TOTAL;

    private getSelectedChannelIndex = () => {
        if (!this.props.selectedChannelFilter) {
            return 0;
        }

        const channelIndex = this.filterChannels.findIndex((c) => c === this.props.selectedChannelFilter);
        return channelIndex >= 0 ? channelIndex : 0;
    };

    private getSafeSelectedChannelFilter = () => {
        if (!this.props.selectedChannelFilter) {
            return this.filterChannels[0] || "";
        }

        const channelIndex = this.filterChannels.findIndex((c) => c === this.props.selectedChannelFilter);
        return channelIndex >= 0 ? this.props.selectedChannelFilter : this.filterChannels[0] || "";
    };

    public render() {
        return (
            <Wrapper>
                {this.getChartHeader()}
                {this.props.showInsights && this.props.selectedMetric === "TrafficShare" ? this.getInsights() : null}
                <FlexColumn
                    ref={(el) => {
                        this.chart = el;
                    }}
                >
                    {this.getChannelsButtons()}
                    <ContentWrapper>
                        {this.getChartContainer()}
                        {this.getLegendsContainer()}
                    </ContentWrapper>
                </FlexColumn>
            </Wrapper>
        );
    }

    private getChartHeader = () => {
        if (this.props.webSource !== devicesTypes.DESKTOP) {
            return null;
        }
        return (
            <ChartHeaderContainer>
                <StyledTabList>
                    <StyledTab {...this.getTabProps("TrafficShare")}>
                        <StyledTabIcon
                            size={"xs"}
                            iconName={"chart-pie"}
                        />
                        {i18nFilter()("mmx.channelanalysis.traffic-share.title")}
                    </StyledTab>
                    <StyledTab {...this.getTabProps("AverageDuration")}>
                        <StyledTabIcon
                            size={"xs"}
                            iconName={"avg-visit-duration"}
                        />
                        {i18nFilter()("mmx.channelanalysis.duration.title")}
                    </StyledTab>
                    <StyledTab {...this.getTabProps("PagesPerVisit")}>
                        <StyledTabIcon
                            size={"xs"}
                            iconName={"pages-per-visit"}
                        />
                        {i18nFilter()("mmx.channelanalysis.page-visits.title")}
                    </StyledTab>
                    <StyledTab {...this.getTabProps("BounceRate")}>
                        <StyledTabIcon
                            size={"xs"}
                            iconName={"bounce-rate-2"}
                        />
                        {i18nFilter()("mmx.channelanalysis.bounce-rate.title")}
                    </StyledTab>
                </StyledTabList>
            </ChartHeaderContainer>
        );
    };

    private getTabProps = (index) => {
        return {
            onClick: () => {
                this.props.handleMetricChange(index);
                allTrackers.dispatchTrackEvent(
                    "Metric Button",
                    "click",
                    `Over Time Graph/Channel Analysis/${index || ""}`,
                );
            },
            selected: this.props.selectedMetric === index,
            key: `tab-${index}`,
        };
    };

    private isPercentage() {
        const { selectedVisualizationIndex, selectedMetric } = this.props;
        return selectedMetric === "TrafficShare" && selectedVisualizationIndex === 0;
    }

    private basePostProcessChartData = (data) => {
        if (this.state.isCompare) {
            return data.map((item) => {
                if (item.data) {
                    const legendItem = this.state.checkboxFilters.filter((i) => i.name === item.name)[0];
                    return {
                        ...item,
                        visible: !legendItem.hidden,
                        color: legendItem.color,
                    };
                }
            });
        } else {
            return data.reverse().map((item, index) => {
                const legendItem = this.state.checkboxFilters[index];
                return {
                    ...item,
                    visible: !legendItem.hidden,
                    color: legendItem.color,
                };
            });
        }
    };

    private shouldShowLineMarkers = (
        granularity: string,
        graphData: { data?: unknown[] }[] | null | undefined,
    ): boolean => {
        const dataPointCount = graphData?.[0]?.data?.length ?? 0;
        return granularity !== "Daily" || dataPointCount <= MAX_DAILY_DATA_POINTS_WITH_MARKERS;
    };

    private getChartContainer = () => {
        const { mtdEndDate, isMTDOn, selectedGranularity, isPoP, durationObject } = this.props;
        const { isCompare } = this.state;
        const { selectedMetric } = this.props;
        let data;
        try {
            data = isPoP ? this.transformDataPoP() : this.getGraphData();
        } catch (e) {
            Sentry.error("error in transform functions in ChannelAnalysisChart.tsx", e);
        }

        if (this.state.isError !== !data) {
            this.setState({ isError: !data });
        }

        const showGoogleAiModeAnnotations =
            !isPoP &&
            shouldShowGoogleAiModeAnnotations(
                isCompare,
                this.getSafeSelectedChannelFilter(),
                (data ?? []).map(({ name }) => name),
            );

        return this.state.isError
            ? ChannelAnalysisChart.getNoData()
            : data && (
                  <ChartContainer>
                      {isPoP ? (
                          isCompare ? (
                              this.renderPopCompareChart(data)
                          ) : (
                              this.renderPopChart(data)
                          )
                      ) : (
                          <ChartWrapper>
                              <LineGraph
                                  // keyed on channel and annotation visibility: Highcharts keeps first-render plot lines on update
                                  key={`${selectedGranularity}-${selectedMetric}-${this.getSafeSelectedChannelFilter()}-${showGoogleAiModeAnnotations}`}
                                  data={data as any}
                                  graphConfig={getChartConfig(
                                      this.isPercentage(),
                                      selectedMetric,
                                      selectedGranularity,
                                      isMTDOn,
                                      mtdEndDate,
                                      durationObject,
                                      isPoP,
                                      data,
                                      this.props.webSource === devicesTypes.DESKTOP,
                                      this.newMMXAlgoStartData,
                                      this.props.getHdBannerCtaState,
                                      showGoogleAiModeAnnotations,
                                  )}
                                  shouldPresentLegend={false}
                                  withMarkers={this.shouldShowLineMarkers(selectedGranularity, data)}
                              />
                          </ChartWrapper>
                      )}
                  </ChartContainer>
              );
    };

    private getLegendsContainer = () => {
        const { isPoP } = this.props;
        const { isCompare } = this.state;
        const titleTooltip = this.getLegendTooltip();

        return (
            <ChartLegendsContainer
                justifyContent={!isCompare && !isPoP ? "center" : null}
                paddingTop={isCompare || isPoP ? 7 : null}
            >
                <CheckBoxContainer>{this.getLegends(titleTooltip)}</CheckBoxContainer>
            </ChartLegendsContainer>
        );
    };

    private getInsights = () => {
        const { selectedGranularity, domain, isLoading, durationObject } = this.props;

        const onInsightClick = (chosenChannel, type, index, domain?) => {
            let checkboxFilters;
            if (domain) {
                this.setState({
                    selectedChannel: this.filterChannels.findIndex((c) => c === chosenChannel),
                });
                this.props.handleChannelFilterChange(chosenChannel);
                checkboxFilters = this.legendItems.map((f) => {
                    f.hidden = f.name !== domain.name;
                    return f;
                });
            } else {
                checkboxFilters = this.legendItems.map((f) => {
                    f.hidden = f.name !== chosenChannel;
                    return f;
                });
            }
            const insights = this.state.insights.map((card) => {
                card.isClicked = card.index === index;
                card.isVisited = card.isVisited ? true : card.index === index;
                return card;
            });

            this.setState({
                checkboxFilters: checkboxFilters,
                selectedVisualizationIndex: 1,
                insights: insights,
                insightCardClicked: true,
                isAllLegendsChecked: false,
            });

            allTrackers.dispatchTrackEvent(
                "Button",
                "click",
                `Over Time Graph/Channel Analysis/Insights/Card/${type || ""}`,
            );
        };

        const resetInsightsAndLegends = () => {
            const insights = this.state.insights.map((card) => {
                card.isClicked = false;
                return card;
            });

            const filterSites = this.legendItems.map((f) => {
                f.hidden = false;
                return f;
            });

            this.setState({
                insights: insights,
                checkboxFilters: filterSites,
                isAllLegendsChecked: true,
            });

            allTrackers.dispatchTrackEvent("Button", "click", "insights reset button was clicked");
        };

        return (
            <InsightsContainer
                domain={domain}
                granularity={selectedGranularity}
                insights={this.state.insights}
                onInsightClick={onInsightClick}
                isFetching={isLoading}
                channelsData={this.state.checkboxFilters}
                resetClickedCard={resetInsightsAndLegends}
                insightCardClicked={this.state.insightCardClicked}
                isAllLegendsChecked={this.state.isAllLegendsChecked}
                durationObject={durationObject}
                isCompare={this.state.isCompare}
            />
        );
    };

    private static getNoData() {
        return (
            <NoDataWrapper>
                <NoDataLandscape
                    title={"global.nodata.notavilable"}
                    subtitle={"global.nodata.notavilable.subtitle"}
                />
            </NoDataWrapper>
        );
    }

    private renderPopChart(chartData) {
        const postProcessChartData = this.basePostProcessChartData;
        const postProcessChartConfig = (config) => {
            const { selectedMetric } = this.props;
            return combineConfigs({ yAxisFormatter }, [
                config,
                yAxisLabelsConfig,
                {
                    tooltip: {
                        formatter() {
                            const reversePointsForTooltip = this.points.slice().reverse();
                            return config.tooltip.formatter.call({
                                points: reversePointsForTooltip,
                            });
                        },
                    },
                },
                selectedMetric === "TrafficShare"
                    ? {
                          yAxis: {
                              min: 0,
                          },
                      }
                    : {},
            ]);
        };
        const yAxisFormatter = ({ value }) => getYaxisFormat(this.props.selectedMetric, value, this.isPercentage());
        const durationObject = this.props.durationObject;
        const durations = [durationObject.forWidget[1], durationObject.forWidget[0]];

        const selectedChannel = this.getSafeSelectedChannelFilter();
        const popData = chartData?.[selectedChannel] ?? {};

        return (
            <ChartWrapper>
                <PeriodOverPeriodChart
                    showLegend={false}
                    type="line"
                    data={popData}
                    legendDurations={durations}
                    yAxisFormatter={yAxisFormatter}
                    postProcessChartData={postProcessChartData}
                    postProcessChartConfig={postProcessChartConfig}
                    metric={this.state.selectedTab}
                />
            </ChartWrapper>
        );
    }

    private renderPopCompareChart(chartData) {
        const postProcessChartData = this.basePostProcessChartData;

        return (
            <ChannelAnalysisPoPCompare
                type="column"
                data={chartData}
                postProcessChartData={postProcessChartData}
                webSource={this.props.webSource}
                dropdownFilter={this.getSafeSelectedChannelFilter()}
                metric={this.props.selectedMetric}
                isPercentage={this.isPercentage()}
            />
        );
    }
    private getChartColorsMap = (): Record<string, string> => {
        const fallbackColors = CHART_COLORS.trafficSourcesColorsBySourceMMX;
        const customColors = this.props.chartColors ?? {};
        const channels = this.filterChannels;

        return channels.reduce(
            (acc, channel) => {
                acc[channel] = customColors[channel] ?? fallbackColors[channel];
                return acc;
            },
            {} as Record<string, string>,
        );
    };
    private getCheckboxFilters = () => {
        if (this.props.isPoP && this.props.keys.length === 1) {
            const durations = this.props.durationObject.forWidget;
            return durations.map((f, i) => {
                return {
                    name: f,
                    hidden: false,
                    color: CHART_COLORS.periodOverPeriod.Total[i],
                };
            });
        } else {
            if (this.props.keys.length > 1) {
                return this.props.keys.map((f, i) => {
                    return {
                        name: f.name,
                        hidden: false,
                        color: f.color,
                    };
                });
            } else {
                const colorMap = this.getChartColorsMap();
                const isHidden = (channelName: string) =>
                    this.props.activeChannels && !this.props.activeChannels.includes(channelName);

                return this.filterChannels.map((f, i) => ({
                    name: f,
                    hidden: isHidden(f),
                    color: colorMap[f],
                }));
            }
        }
    };

    private getFormattedNumbers = (metric, value) => {
        switch (metric) {
            case "TrafficShare":
                return minVisitsAbbrFilter()(value);
            case "AverageDuration":
                return timeFilter()(value, null);
            case "PagesPerVisit":
                return numberFilter()(value, 2);
            case "BounceRate":
                return percentageSignFilter()(value, 2);
        }
    };

    private transformData = () => {
        const { isCompare } = this.state;
        const { webSource, selectedGranularity, data, selectedMetric } = this.props;
        const safeSelectedChannelFilter = this.getSafeSelectedChannelFilter();

        const Path =
            webSource === devicesTypes.DESKTOP
                ? data?.[gran[selectedGranularity]]?.[selectedMetric]
                : data?.Data?.[selectedMetric];
        const finaleData =
            Path &&
            (webSource === devicesTypes.DESKTOP
                ? isCompare
                    ? Path[safeSelectedChannelFilter]
                    : Path.Data
                : isCompare
                  ? Path[safeSelectedChannelFilter]
                  : Path.Data);
        return finaleData &&
            Object.keys(finaleData).length > 0 &&
            (webSource === devicesTypes.MOBILE || webSource === devicesTypes.TOTAL)
            ? removeDataFromGraphData(finaleData, this.props.startDate)
            : finaleData;
    };

    private constructPopDataObject() {
        const data = this.props.data;
        const metric = this.props.selectedMetric;
        const safeSelectedChannelFilter = this.getSafeSelectedChannelFilter();
        if (!data) {
            return {};
        }
        return this.state.isCompare
            ? {
                  comparedData: data.ComparedData?.Data?.[metric]?.[safeSelectedChannelFilter],
                  Total: data.Data?.[metric]?.[safeSelectedChannelFilter]?.Total,
                  BreakDown: data.Data?.[metric]?.[safeSelectedChannelFilter]?.BreakDown,
              }
            : {
                  comparedData: data.ComparedData?.Data?.[metric],
                  Total: data.Data?.[metric]?.Data?.Total,
                  BreakDown: data.Data?.[metric]?.Data?.BreakDown,
              };
    }

    private transformDataPoP() {
        const data = this.constructPopDataObject();
        return this.state.isCompare
            ? adaptCompareData(data, this.props.webSource)
            : adaptCompareSingleData(data, this.props.webSource);
    }

    private getPopLegendDataObject() {
        const [{ name: thisPeriod }, { name: previousPeriod }] = this.getCheckboxFilters();
        const { Total, comparedData } = this.constructPopDataObject();
        const channel = this.getSafeSelectedChannelFilter();
        return {
            [thisPeriod]: Total?.[channel] ?? 0,
            [previousPeriod]: comparedData?.Data?.Total?.[channel] ?? 0,
        };
    }

    private getLegendTooltip = () => {
        const { startDate, endDate, mtdStartDate, mtdEndDate, isMTDOn, selectedMetric } = this.props;

        if (isMTDOn) {
            const format = "YYYY-MM-DD";
            const getDateString = (start, end) =>
                dayjs(start, format).format("MM YYYY") === dayjs(end, format).format("MM YYYY")
                    ? `${dayjs(start, format).format("MMM")}`
                    : `${dayjs(start, format).format("MMM")} - ${dayjs(end, format).format("MMM")}`;
            const dates = getDateString(startDate, endDate);
            const mtdDates = getDateString(mtdStartDate, mtdEndDate);
            return selectedMetric === "TrafficShare"
                ? i18nFilter()("mmx.channelanalysis.legends.tooltip.mtd.sum", {
                      dates,
                      mtdDates,
                  })
                : i18nFilter()("mmx.channelanalysis.legends.tooltip.mtd.avg", {
                      dates,
                      mtdDates,
                  });
        } else {
            return false;
        }
    };

    private getLegendItems = () => {
        let { checkboxFilters } = this.state;
        if (this.isTotalSource || this.isMobileSource) {
            checkboxFilters = isDurationAfterSplit(this.props.startDate)
                ? removeSearchChannelTotal(checkboxFilters)
                : removeSplitSearchChannelsTotal(checkboxFilters);
        }
        const { selectedMetric, selectedVisualizationIndex } = this.props;
        const data = this.props.isPoP ? this.getPopLegendDataObject() : this.transformData()?.Total;
        if (data) {
            const getWinner = () => {
                if (!data) {
                    return null;
                }
                if (selectedMetric === "BounceRate") {
                    return _.minBy(Object.keys(data), (o) => {
                        return data[o];
                    });
                } else {
                    return _.maxBy(Object.keys(data), (o) => {
                        return data[o];
                    });
                }
            };
            const winner = getWinner();
            const isPercentage = selectedMetric === "TrafficShare" && selectedVisualizationIndex === 0;
            const total: any = Object.values(data).reduce((acc: number, val: number) => acc + val, 0);

            return total
                ? checkboxFilters.map((item) => {
                      const value =
                          data[item.name] && isPercentage
                              ? percentageSignFilter()(data[item.name] / total, 2)
                              : data[item.name]
                                ? this.getFormattedNumbers(selectedMetric, data[item.name])
                                : "N/A";
                      return {
                          data: value,
                          name: item.name,
                          hidden: item.hidden,
                          color: item.color,
                          isWinner: item.name === winner,
                      };
                  })
                : [];
        } else {
            return [];
        }
    };

    private getLegendItemsPoP = () => {
        const { checkboxFilters } = this.state;
        return checkboxFilters.map((item) => {
            return {
                name: item.name,
                hidden: item.hidden,
                color: item.color,
            };
        });
    };

    private getLegends = (titleTooltip) => {
        const { selectedMetric } = this.props;
        this.legendItems = this.props.isPoP && this.state.isCompare ? this.getLegendItemsPoP() : this.getLegendItems();

        if (this.props.isPoP && !this.state.isCompare && this.legendItems.length > 0) {
            this.legendItems = [this.legendItems[1], this.legendItems[0]];
        }

        const onLegendClick = (filter) => {
            const action = filter.hidden ? "add" : "remove";
            let isAllLegendsChecked = true;
            let filterSites = this.legendItems.map((f) => {
                if (f.name === filter.name) {
                    f.hidden = !f.hidden;
                }
                if (f.hidden) {
                    isAllLegendsChecked = false;
                }
                return f;
            });

            const insights = this.state.insights.map((card) => {
                card.isClicked = false;
                return card;
            });

            if (this.props.isPoP && !this.state.isCompare) {
                filterSites = [filterSites[1], filterSites[0]];
            }

            this.setState({
                checkboxFilters: filterSites,
                insights: insights,
                isAllLegendsChecked: isAllLegendsChecked,
            });

            allTrackers.dispatchTrackEvent(
                "Graph Filter",
                "click",
                `${selectedMetric || ""}/${filter.name || ""}/${action || ""}`,
            );
        };
        return (
            this.legendItems &&
            this.legendItems.length > 0 && (
                <>
                    <LegendsTitle>
                        {i18nFilter()(
                            this.state.isCompare
                                ? "website_analysis.marketing_channels.channel_analysis.legends.websites.title"
                                : "website_analysis.marketing_channels.channel_analysis.legends.channels.title",
                        )}
                        {titleTooltip !== false ? (
                            <PlainTooltip
                                placement="top"
                                text={titleTooltip}
                            >
                                <span>
                                    <InfoIcon iconName="info" />
                                </span>
                            </PlainTooltip>
                        ) : null}
                        {this.props.isShowMMXAlertBell ? <MMXAlertWithPlainTooltip /> : null}
                    </LegendsTitle>
                    <Legends
                        legendComponent={LegendWithOneLineCheckboxFlex}
                        legendComponentWrapper={StyledLegendWrapper}
                        legendItems={this.legendItems}
                        toggleSeries={onLegendClick}
                        gridDirection="column"
                        textMaxWidth={
                            window.innerWidth < 1680 ? (window.innerWidth > 1366 ? "125px" : "100px") : "150px"
                        }
                    />
                </>
            )
        );
    };

    private getChannelsButtons() {
        const isAllowed = this.state.isCompare || this.props.isPoP;
        if (!isAllowed) {
            return null;
        }
        const onChannelChange = (index: number) => {
            this.setState({ selectedChannel: index });
            this.props.handleChannelFilterChange(this.filterChannels[index]);
            allTrackers.dispatchTrackEvent(
                "Graph Filter",
                "click",
                `channel analysis tab - ${this.filterChannels[index] || ""}`,
            );
        };

        const i18n = i18nFilter();
        const segmentedValues = this.filterChannels.map((channelName) => {
            const shortKey = CHANNEL_SHORT_LABEL_KEY[channelName];
            return shortKey ? i18n(shortKey) : channelName;
        });

        return (
            <ChannelsBar data-automation="channel-analysis-channels-segmented">
                <SegmentedControl
                    values={segmentedValues}
                    selectedIndex={this.state.selectedChannel}
                    onChange={onChannelChange}
                />
            </ChannelsBar>
        );
    }

    private getGraphData = () => {
        const { selectedVisualizationIndex, selectedMetric } = this.props;
        const isPercentage = selectedMetric === "TrafficShare" && selectedVisualizationIndex === 0;
        const filters = [];
        this.state.checkboxFilters.forEach((f) => {
            if (!f.hidden) {
                filters.push(f);
            }
        });
        const data = this.transformData()?.BreakDown;
        let newData;
        let isAllValuesNull = true;

        if (data) {
            const sortedData = Object.keys(data)
                .sort()
                .reduce((obj, key) => {
                    obj[key] = data[key];
                    return obj;
                }, {});
            newData = filters.map((f) => {
                return {
                    name: f.name,
                    color: f.color,
                    data: Object.keys(sortedData).map((d) => {
                        const value = sortedData[d][f.name];
                        const total: any = Object.values(sortedData[d]).reduce(
                            (acc: number, val: number) => acc + val,
                            0,
                        );

                        if (value) {
                            isAllValuesNull = false;
                        }

                        return [dayjs.utc(d).valueOf(), isPercentage ? value / total : value];
                    }),
                };
            });
        }

        return isAllValuesNull ? null : newData;
    };

    public downloadPng = (): Promise<void> => {
        const metric = this.props.selectedMetric;
        allTrackers.dispatchTrackEvent("Download", "submit-ok", `${metric || ""}/PNG`);

        const chartElement = this.chart?.current || this.chart;
        if (!chartElement) {
            console.error("Chart element not found for PNG export");
            return Promise.resolve();
        }
        const styleHTML = this.pdfExportService.getStylesheetsForPng();

        return this.pdfExportService.downloadHtmlPngFedService(
            styleHTML + chartElement.outerHTML,
            "Marketing Channels Analysis",
            chartElement.clientWidth || 1366,
            chartElement.clientHeight || 400,
        );
    };

    private getEnrichedInsights = () => {
        if (!this.props.insights) {
            return [];
        }
        return this.props.insights.map((card: IInsight, index: number) => ({
            ...card,
            isClicked: false,
            index,
        }));
    };
}
